import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionApi } from '../api/subscriptionApi';
import { userManagementApi } from '../api/userManagementApi';
import { AdminCreateUserRequest, AdminUpdateUserRequest, AssignAddOnsRequest, AssignTierRequest, Guid, TierAddOnDto, UpdateAddOnDto, UpdateTierDto, UserListQuery, UserStatusUpdateRequest } from '../types/userManagement';

const USERS_KEY = ['admin-users'];

export function useUsers(query: UserListQuery) {
  return useQuery({
    queryKey: [...USERS_KEY, query],
    queryFn: () => userManagementApi.getUsers(query),
  });
}

export function useUserDetails(userId?: Guid) {
  return useQuery({
    queryKey: [...USERS_KEY, userId],
    queryFn: () => userManagementApi.getUser(userId!),
    enabled: Boolean(userId),
  });
}

export function useUserAudit(userId?: Guid) {
  return useQuery({
    queryKey: [...USERS_KEY, userId, 'audit'],
    queryFn: () => userManagementApi.getAudit(userId!),
    enabled: Boolean(userId),
  });
}

export function useUserSubscription(userId?: Guid) {
  return useQuery({
    queryKey: [...USERS_KEY, userId, 'subscription'],
    queryFn: () => subscriptionApi.getUserSubscription(userId!),
    enabled: Boolean(userId),
  });
}

export function useUserSubscriptionPricing(userId?: Guid) {
  return useQuery({
    queryKey: [...USERS_KEY, userId, 'subscription', 'pricing'],
    queryFn: () => subscriptionApi.getUserSubscriptionPricing(userId!),
    enabled: Boolean(userId),
  });
}

export function useSubscriptionTiers() {
  return useQuery({
    queryKey: ['subscription-tiers'],
    queryFn: subscriptionApi.getTiers,
  });
}

export function useSubscriptionAddOns() {
  return useQuery({
    queryKey: ['subscription-addons'],
    queryFn: subscriptionApi.getAddOns,
  });
}

export function useTierAddOns(tierId?: number) {
  return useQuery({
    queryKey: ['subscription-tier-addons', tierId],
    queryFn: () => subscriptionApi.getTierAddOns(tierId!),
    enabled: tierId !== undefined,
  });
}

export function useUserManagementMutations() {
  const queryClient = useQueryClient();
  const refreshUsers = () => queryClient.invalidateQueries({ queryKey: USERS_KEY });
  const refreshSubscription = (userId: Guid) => {
    refreshUsers();
    queryClient.invalidateQueries({ queryKey: [...USERS_KEY, userId, 'subscription'] });
    queryClient.invalidateQueries({ queryKey: [...USERS_KEY, userId, 'subscription', 'pricing'] });
  };
  const refreshTiers = () => queryClient.invalidateQueries({ queryKey: ['subscription-tiers'] });
  const refreshAddOns = () => queryClient.invalidateQueries({ queryKey: ['subscription-addons'] });
  const refreshTierAddOns = (tierId: number) => queryClient.invalidateQueries({ queryKey: ['subscription-tier-addons', tierId] });

  return {
    createUser: useMutation({
      mutationFn: (request: AdminCreateUserRequest) => userManagementApi.createUser(request),
      onSuccess: refreshUsers,
    }),
    updateStatus: useMutation({
      mutationFn: ({ userId, request }: { userId: Guid; request: UserStatusUpdateRequest }) =>
        userManagementApi.updateStatus(userId, request),
      onSuccess: refreshUsers,
    }),
    resendInvite: useMutation({ mutationFn: userManagementApi.resendInvite, onSuccess: refreshUsers }),
    unlock: useMutation({ mutationFn: userManagementApi.unlock, onSuccess: refreshUsers }),
    forceReset: useMutation({ mutationFn: userManagementApi.forceReset, onSuccess: refreshUsers }),
    updateUser: useMutation({
      mutationFn: ({ userId, request }: { userId: Guid; request: AdminUpdateUserRequest }) =>
        userManagementApi.updateUser(userId, request),
      onSuccess: refreshUsers,
    }),
    updateUserSubscriptionTier: useMutation({
      mutationFn: ({ userId, request }: { userId: Guid; request: AssignTierRequest }) =>
        subscriptionApi.updateUserSubscriptionTier(userId, request),
      onSuccess: (_, { userId }) => refreshSubscription(userId),
    }),
    assignUserAddOns: useMutation({
      mutationFn: ({ userId, request }: { userId: Guid; request: AssignAddOnsRequest }) =>
        subscriptionApi.assignUserAddOns(userId, request),
      onSuccess: (_, { userId }) => refreshSubscription(userId),
    }),
    removeUserAddOn: useMutation({
      mutationFn: ({ userId, addOnId }: { userId: Guid; addOnId: number }) =>
        subscriptionApi.removeUserAddOn(userId, addOnId),
      onSuccess: (_, { userId }) => refreshSubscription(userId),
    }),
    createTier: useMutation({ mutationFn: subscriptionApi.createTier, onSuccess: refreshTiers }),
    updateTier: useMutation({ mutationFn: ({ tierId, request }: { tierId: number; request: UpdateTierDto }) => subscriptionApi.updateTier(tierId, request), onSuccess: refreshTiers }),
    deleteTier: useMutation({ mutationFn: subscriptionApi.deleteTier, onSuccess: refreshTiers }),
    createAddOn: useMutation({ mutationFn: subscriptionApi.createAddOn, onSuccess: refreshAddOns }),
    updateAddOn: useMutation({ mutationFn: ({ addOnId, request }: { addOnId: number; request: UpdateAddOnDto }) => subscriptionApi.updateAddOn(addOnId, request), onSuccess: refreshAddOns }),
    deleteAddOn: useMutation({ mutationFn: subscriptionApi.deleteAddOn, onSuccess: refreshAddOns }),
    assignTierAddOn: useMutation({ mutationFn: ({ tierId, request }: { tierId: number; request: TierAddOnDto }) => subscriptionApi.assignTierAddOn(tierId, request), onSuccess: (_, { tierId }) => refreshTierAddOns(tierId) }),
    removeTierAddOn: useMutation({ mutationFn: ({ tierId, addOnId }: { tierId: number; addOnId: number }) => subscriptionApi.removeTierAddOn(tierId, addOnId), onSuccess: (_, { tierId }) => refreshTierAddOns(tierId) }),
  };
}